import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, Plus } from "lucide-react";
import { useTestUsers, type TestUser } from "@/hooks/use-test-users";

const EXAMPLE_USERS: Omit<TestUser, "id">[] = [
  {
    email: "handerson@fhservitec.com.br",
    role: "admin",
    password: "teste123",
    empresa: "FH Serviços Técnicos",
  },
  {
    email: "teste@gmail.com",
    role: "parceiro",
    password: "teste123",
    empresa: "DESMONTE N.S.A",
    vistoriadora: "FH Serviços Técnicos",
  },
];

export interface TestUserManagerProps {
  onSelectUser: (email: string, password: string) => Promise<void>;
  isLoading?: boolean;
}

export function TestUserManager({
  onSelectUser,
  isLoading = false,
}: TestUserManagerProps) {
  const { users, addUser, addUsers, deleteUser } = useTestUsers();
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TestUser | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState<Omit<TestUser, "id">>({
    email: "",
    role: "admin",
    password: "",
    empresa: "",
    empresaId: "",
    vistoriadora: "",
  });

  const handleAddUser = () => {
    if (!formData.email || !formData.password) {
      alert("Email e senha são obrigatórios");
      return;
    }
    addUser(formData);
    setFormData({
      email: "",
      role: "admin",
      password: "",
      empresa: "",
      empresaId: "",
      vistoriadora: "",
    });
  };

  const handleSelectUser = async (user: TestUser) => {
    setSelectedUser(user);
    setShowConfirm(true);
  };

  const handleConfirmLogin = async () => {
    if (selectedUser) {
      await onSelectUser(selectedUser.email, selectedUser.password);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            🧪 Usuários Teste
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerenciar Usuários de Teste</DialogTitle>
            <DialogDescription>
              Adicione, visualize e teste usuários em modo DEBUG
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Form to add new user */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h3 className="font-semibold">Novo Usuário de Teste</h3>
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    addUsers(EXAMPLE_USERS);
                    alert("✅ Todos os usuários de exemplo foram adicionados!");
                  }}
                  className="bg-green-100 text-green-700 hover:bg-green-200"
                >
                  📋 Carregar Exemplos
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="text"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(role: any) =>
                      setFormData({ ...formData, role })
                    }
                  >
                    <SelectTrigger id="role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="rt">RT (Avaliador)</SelectItem>
                      <SelectItem value="tv">TV (Inspector)</SelectItem>
                      <SelectItem value="cliente">Cliente</SelectItem>
                      <SelectItem value="parceiro">Parceiro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empresa">Empresa</Label>
                  <Input
                    id="empresa"
                    placeholder="Nome da empresa (opcional)"
                    value={formData.empresa}
                    onChange={(e) =>
                      setFormData({ ...formData, empresa: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="empresaId">Empresa ID</Label>
                  <Input
                    id="empresaId"
                    placeholder="UUID da empresa (opcional)"
                    value={formData.empresaId}
                    onChange={(e) =>
                      setFormData({ ...formData, empresaId: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vistoriadora">Vistoriadora</Label>
                  <Input
                    id="vistoriadora"
                    placeholder="Vistoriadora vinculada (opcional)"
                    value={formData.vistoriadora}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vistoriadora: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <Button onClick={handleAddUser} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Usuário
              </Button>
            </div>

            {/* Users table */}
            {users.length > 0 ? (
              <div className="space-y-2">
                <h3 className="font-semibold">
                  Usuários Salvos ({users.length})
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Vistoriadora</TableHead>
                        <TableHead className="w-20">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow
                          key={user.id}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleSelectUser(user)}
                        >
                          <TableCell className="font-mono text-sm">
                            {user.email}
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                              {user.role}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {user.empresa || "—"}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {user.vistoriadora || "—"}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteUser(user.id);
                              }}
                              className="p-1 hover:bg-red-100 rounded transition-colors"
                              title="Deletar usuário"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Nenhum usuário de teste ainda. Adicione um acima.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog for login */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Login</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser && (
                <div className="space-y-2 text-left mt-4">
                  <div>
                    <span className="font-semibold">Email:</span>{" "}
                    {selectedUser.email}
                  </div>
                  <div>
                    <span className="font-semibold">Role:</span>{" "}
                    {selectedUser.role}
                  </div>
                  {selectedUser.empresa && (
                    <div>
                      <span className="font-semibold">Empresa:</span>{" "}
                      {selectedUser.empresa}
                    </div>
                  )}
                  {selectedUser.vistoriadora && (
                    <div>
                      <span className="font-semibold">Vistoriadora:</span>{" "}
                      {selectedUser.vistoriadora}
                    </div>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmLogin}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90"
            >
              {isLoading ? "Entrando..." : "Confirmar Login"}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
